"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import type { IHeroBlock } from "@/lib/sanity/types";
import { MotionReveal } from "@/components/marketing/MotionReveal";
import { AnimatedButton } from "@/components/marketing/AnimatedButton";
import {
  HeroBackdropDepthLayer,
  type THeroBackdropDepthLayerTheme,
} from "@/components/marketing/HeroBackdropDepthLayer";

interface IHeroSectionProps {
  hero: IHeroBlock;
  /** Optional secondary CTA (e.g. "Sign In") */
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  /** Extra content rendered below the CTAs (e.g. taglines). */
  children?: React.ReactNode;
  /** Optional customization for the green “depth orb” backdrop. */
  backdropTheme?: Partial<THeroBackdropDepthLayerTheme>;
  /** Enables horizontal full-bleed backdrop rendering. */
  backdropFullBleed?: boolean;

  /**
   * Layout variant:
   * - centered: current marketing hero style
   * - split: text/content on the left + a rightSlot card (conversion-focused)
   */
  layout?: "centered" | "split";
  /** Right-side content for `layout="split"` (e.g. waitlist card). */
  rightSlot?: React.ReactNode;
  /** Enables the richer “hero backdrop” layer (used on the home page). */
  showBackdrop?: boolean;
  /** Replaces the default primary CTA link when provided. */
  primaryCta?: React.ReactNode;
}

export function HeroSection({
  hero,
  secondaryCtaText,
  secondaryCtaLink,
  children,
  layout = "centered",
  rightSlot,
  showBackdrop = false,
  backdropTheme,
  backdropFullBleed,
  primaryCta,
}: IHeroSectionProps): React.ReactElement {
  const isSplit = layout === "split" && rightSlot != null;
  const sheenRgb = backdropTheme?.orb1?.rgb ?? { r: 79, g: 142, b: 110 };

  return (
    <section
      className={[
        "relative py-16 md:py-24",
        isSplit ? "text-left" : "text-center",
      ].join(" ")}
      aria-label="Hero"
    >
      {showBackdrop && (
        <>
          {/* Subtle green depth (floating orbs). */}
          <HeroBackdropDepthLayer
            fullBleed={backdropFullBleed ?? true}
            {...(backdropTheme != null ? { theme: backdropTheme } : {})}
          />
          {/* Radial “mauve -> mint” glow blobs. */}
          <div className="pointer-events-none absolute -right-24 -top-24 h-[340px] w-[340px] rounded-full bg-primary/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-20 top-10 h-[300px] w-[300px] rounded-full bg-secondary/60 blur-3xl" />
          {/* Gentle top-to-bottom sheen. */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-1/2 -z-10"
            style={{
              backgroundImage: `radial-gradient(ellipse_at_50%_0%,rgba(${sheenRgb.r},${sheenRgb.g},${sheenRgb.b},0.18),transparent_60%)`,
            }}
          />
        </>
      )}

      <div
        className={[
          "mx-auto w-full max-w-5xl",
          isSplit ? "grid items-start gap-10 lg:grid-cols-2" : "",
        ].join(" ")}
      >
        <div className={isSplit ? "" : "mx-auto max-w-3xl"}>
          <MotionReveal y={8}>
            <h1 className="text-4xl font-bold tracking-tight text-foreground md:text-6xl">
              {hero.heading}
            </h1>
          </MotionReveal>

          {hero.subheading && (
            <MotionReveal delay={0.05}>
              <p
                className={[
                  "mt-4 max-w-2xl text-xl text-muted-foreground md:text-2xl",
                  isSplit ? "" : "mx-auto",
                ].join(" ")}
              >
                {hero.subheading}
              </p>
            </MotionReveal>
          )}

          {hero.description && (
            <MotionReveal delay={0.1}>
              <p
                className={[
                  "mt-4 max-w-xl text-lg text-muted-foreground",
                  isSplit ? "" : "mx-auto",
                ].join(" ")}
              >
                {hero.description}
              </p>
            </MotionReveal>
          )}

          {(hero.ctaText || secondaryCtaText) && (
            <MotionReveal delay={0.15}>
              <div
                className={[
                  "mt-8 flex flex-wrap items-center gap-4",
                  isSplit ? "justify-start" : "justify-center",
                ].join(" ")}
              >
                {hero.ctaText && (primaryCta ?? hero.ctaLink) && (
                  <AnimatedButton>
                    {primaryCta ?? (
                      <Button asChild size="lg">
                        <Link href={hero.ctaLink!}>{hero.ctaText}</Link>
                      </Button>
                    )}
                  </AnimatedButton>
                )}
                {secondaryCtaText && secondaryCtaLink && (
                  <AnimatedButton>
                    <Button asChild variant="secondary" size="lg">
                      <Link href={secondaryCtaLink}>{secondaryCtaText}</Link>
                    </Button>
                  </AnimatedButton>
                )}
              </div>
            </MotionReveal>
          )}

          {children}
        </div>

        {isSplit && <div className="lg:sticky lg:top-24">{rightSlot}</div>}
      </div>
    </section>
  );
}
